import React, { useEffect, useMemo, useRef } from 'react';
import { displayParts, escapeStrayLessThan } from '../promptText';
/**
 * MathJax loads asynchronously (public/index.html), and typesetting before it
 * is ready does nothing. The shared helper returns early in that case and
 * never retries, so a caption shown soon after the page loaded kept its raw
 * "$G$" for good. Wait for it, then typeset just this element.
 */
const MATHJAX_WAIT_MS = 30000;

function mathJaxReady() {
  const mj = typeof window !== 'undefined' ? window.MathJax : null;
  if (mj?.typesetPromise) return (mj.startup?.promise || Promise.resolve()).catch(() => {});
  return new Promise((resolve) => {
    const started = Date.now();
    const tick = setInterval(() => {
      const m = window.MathJax;
      if (m?.typesetPromise || Date.now() - started > MATHJAX_WAIT_MS) {
        clearInterval(tick);
        (m?.startup?.promise || Promise.resolve()).then(resolve, resolve);
      }
    }, 200);
  });
}

export async function typesetWhenReady(el) {
  if (!el) return;
  await mathJaxReady();
  if (!el.isConnected || !window.MathJax?.typesetPromise || el.querySelector('mjx-container')) return;
  try {
    await window.MathJax.typesetPromise([el]);
  } catch {
    /* left as written: readable, just not typeset */
  }
}

/**
 * Authored question text, as a student reads it. Two sources: `html` is the
 * paper's own markup (<p>, <em>, <sup>...), `text` is the feedback API's
 * display text (paragraphs separated by blank lines). Either may carry LaTeX
 * ($...$), which MathJax typesets once the text is on the page.
 *
 * The markup comes from our own corpus, but it is still reduced to a short
 * list of formatting tags with no attributes before it is rendered, so
 * nothing in a question can carry a link, a style or a script onto the page.
 */
const ALLOWED = new Set(['P', 'EM', 'STRONG', 'B', 'I', 'SUP', 'SUB', 'BR', 'UL', 'OL', 'LI']);
const DROPPED = new Set(['SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'TEMPLATE']);

function escapeHtml(s) {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

export function cleanAuthoredHtml(html) {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(`<body>${escapeStrayLessThan(html)}</body>`, 'text/html');
  const walk = (node) => {
    Array.from(node.childNodes).forEach((child) => {
      if (child.nodeType !== 1) {
        if (child.nodeType !== 3) child.remove();
        return;
      }
      if (DROPPED.has(child.tagName)) {
        child.remove();
        return;
      }
      walk(child);
      if (ALLOWED.has(child.tagName)) {
        Array.from(child.attributes).forEach((a) => child.removeAttribute(a.name));
      } else {
        child.replaceWith(...Array.from(child.childNodes));
      }
    });
  };
  walk(doc.body);
  return doc.body.innerHTML;
}

export function textToHtml(text) {
  return (text || '')
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => `<p>${escapeHtml(p)}</p>`)
    .join('');
}

export default function RichText({ html, text, className = '' }) {
  const ref = useRef(null);
  const markup = useMemo(
    () => (html != null ? cleanAuthoredHtml(html) : textToHtml(text)),
    [html, text],
  );

  // The same object on every render: React 19 rewrites the HTML whenever it
  // is handed a new one, which undid MathJax's typesetting on every tick of
  // the interview timer.
  const inner = useMemo(() => ({ __html: markup }), [markup]);

  useEffect(() => {
    if (markup.includes('$') || markup.includes('\\')) typesetWhenReady(ref.current);
  }, [markup]);

  return (
    <div
      ref={ref}
      className={`iv-rich mathjax-process ${className}`.trim()}
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={inner}
    />
  );
}

/** One line the examiner said, shown from the authored markup it came from
 * where it can be matched (promptText.js), as text otherwise. */
export function PromptLine({ question, said, className = '' }) {
  const parts = displayParts(question, said);
  return (
    <div className={className}>
      {parts.map((part, i) => (part.html != null
        // eslint-disable-next-line react/no-array-index-key
        ? <RichText key={i} html={part.html} />
        // eslint-disable-next-line react/no-array-index-key
        : <RichText key={i} text={part.text} />))}
    </div>
  );
}
